import type { TicketTierDto, TicketRedemptionDto } from '../api/tickets.api';

/**
 * Starea unui nivel de tichet pentru UI (spec §8).
 *
 * Union DISCRIMINAT, nu boolean si nici union de string-uri simple: cele patru stari raspund la
 * intrebari diferite - „ce pot face?", „cand?", „niciodata?" - si fiecare are nevoie de alt payload
 * ca sa fie afisata. Un `'closed'` sec ar fi obligat apelantul sa se intoarca la `tier` dupa date,
 * si ar fi cerut un refactor exact cand incep task-urile 12/13.
 */
export type TierState =
    | { kind: 'available' }
    | { kind: 'insufficient'; missingPoints: number }
    | { kind: 'outside-window'; phase: 'before' | 'after'; availableFrom?: string; availableTo?: string }
    | { kind: 'limit-reached'; maxPerSchool: number };

/**
 * Starile care CONSUMA un slot din maxPerSchool. Oglindeste `TicketRedemptionStatus.CONSUMA_PUNCTE`
 * din backend. SE-18: CONFIRMED consuma si el — lipsa lui de aici ar fi aratat un premiu confirmat ca
 * slot liber, deci un buton activ pe care backendul il refuza.
 */
const STARI_CARE_CONSUMA: ReadonlyArray<TicketRedemptionDto['status']> = ['REQUESTED', 'CONFIRMED', 'FULFILLED'];

/**
 * Ziua locala ca `YYYY-MM-DD`, pentru comparatie cu LocalDate-ul backendului.
 *
 * Nu `toISOString()`: acela converteste in UTC, iar businessul e in Europe/Bucharest (+2/+3). Pe
 * 15 octombrie la 01:00 ora Romaniei, `toISOString()` da inca „2026-10-14" - adica un nivel care
 * se deschide azi ar aparea inchis pana la 3 dimineata.
 */
function ziua(d: Date): string {
    const an = d.getFullYear();
    const luna = String(d.getMonth() + 1).padStart(2, '0');
    const zi = String(d.getDate()).padStart(2, '0');
    return `${an}-${luna}-${zi}`;
}

/**
 * availableFrom/availableTo sunt declarate `Date` in types.gen.ts si MINT: la runtime sunt
 * string-uri ISO `YYYY-MM-DD`, pentru ca backendul trimite LocalDate iar transformers.gen.ts nu
 * ruleaza niciodata (pct. 4 din CLAUDE.md). Comparatia lexicografica pe `YYYY-MM-DD` e corecta si
 * echivalenta cu cea cronologica - formatul e zero-padded si de latime fixa.
 */
function caData(v: TicketTierDto['availableFrom']): string | undefined {
    return (v as unknown as string) || undefined;
}

/** Cate puncte mai trebuie ca sa ajunga soldul. Niciodata negativ. */
export function missingPoints(tier: TicketTierDto, available: number): number {
    return Math.max(0, (tier.pointsCost ?? 0) - available);
}

/**
 * Prioritatea: fereastra > limita > sold > disponibil.
 *
 * De ce fereastra prima: n-are sens sa-i ceri cuiva sa mai adune 200 de puncte pentru un nivel
 * care s-a inchis definitiv. De ce limita inaintea soldului: un nivel epuizat nu se deblocheaza
 * cu puncte, deci „iti mai trebuie N" ar fi o minciuna utila nimanui.
 *
 * Capetele ferestrei sunt INCLUSIVE, ca in backend (TicketService). Un `<` in loc de `<=` aici ar
 * refuza in UI exact ultima zi pe care serverul o accepta - genul de off-by-one care nu se vede.
 *
 * Nu e o poarta de securitate: backendul re-verifica tot la POST /api/ticket-redemptions. Aici
 * decidem doar ce scrie pe card.
 */
export function computeTierState(
    tier: TicketTierDto,
    available: number,
    ownRedemptions: TicketRedemptionDto[],
    today: Date = new Date(),
): TierState {
    const azi = ziua(today);
    const dela = caData(tier.availableFrom);
    const panaLa = caData(tier.availableTo);

    // `phase` se decide AICI, nu in UI. Functia oricum compara datele ca sa constate ca esti in
    // afara ferestrei, deci stie deja pe ce parte esti; a arunca informatia si a o recalcula in
    // componenta ar muta o decizie de business intr-un fisier fara niciun test.
    // „inainte" e o promisiune cu data; „dupa" e definitiv si NU are data viitoare de aratat.
    if (dela && azi < dela) {
        return { kind: 'outside-window', phase: 'before', availableFrom: dela, availableTo: panaLa };
    }
    if (panaLa && azi > panaLa) {
        return { kind: 'outside-window', phase: 'after', availableTo: panaLa };
    }

    if (tier.maxPerSchool != null) {
        const folosite = ownRedemptions.filter(
            (r) => r.tierId === tier.id && STARI_CARE_CONSUMA.includes(r.status),
        ).length;
        if (folosite >= tier.maxPerSchool) {
            return { kind: 'limit-reached', maxPerSchool: tier.maxPerSchool };
        }
    }

    const lipsa = missingPoints(tier, available);
    if (lipsa > 0) return { kind: 'insufficient', missingPoints: lipsa };

    return { kind: 'available' };
}

/**
 * Formateaza un `LocalDate` (`YYYY-MM-DD`) ca zi in romana, FARA ora.
 *
 * De ce nu `formatUnixTimestamp`: acela adauga mereu ora, iar pe un LocalDate ora nu exista.
 * `new Date("2026-11-01")` se parseaza ca miezul noptii UTC, deci in Bucuresti iese
 * „1 noiembrie 2026 la 02:00" - o ora inventata, deplasata de fus. Pentru fereastra de
 * disponibilitate a unui tichet, ziua e tot ce inseamna ceva.
 *
 * Componentele din `YYYY-MM-DD` se despart manual, tocmai ca sa nu treaca prin parsarea UTC.
 */
export function formatZi(iso: string | undefined): string | null {
    if (!iso) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
}
